import { useMemo } from 'react'
import { BuyDownloadButtonCSV, BuyDownloadButtonPDF, BuyDownloadPdfTemplateButton, BuyDownloadTags, BuyPdfPreview, BuySupplierPdfPreview } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderBuyLayout/BuyButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { BuyInvoiceDuplicateButton } from '../../../BuyInvoiceButtons'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'

const BuyInvoiceFinishedStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useBuyInvoiceFinishedButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useBuyInvoiceFinishedButtonsLogic = ({ data, onSubmit, isReadOnly, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'buy_invoice_finished_step_buttons'
    const Order = data?.header

    const defaultButtons = [{ "id": "previewBuyInvoicePDF" }, { "id": "downloadBuyInvoice" }, { "id": "duplicateBuyInvoice" }]

    const buyInvoiceDuplicateText = t('duplicateBuyInvoice', 'Duplicar factura de compra')
    const buyInvoiceDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const buyInvoiceDownloadTagsText = t('printTags', 'Imprimir etiquetas')
    const buyInvoicePreviewPDFText = t('previewPdfTemplate', 'Previsualizar plantilla PDF')
    const buyInvoiceDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const buyInvoiceDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const buyInvoiceDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const buyInvoiceVariablesText = t('variables', 'Variables')
    const buyOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')
    const buyOrderPreviewSupplierPDFText = t('previewSupplierPDF', 'Previsualizar PDF proveedor')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateBuyInvoice: {
                component: BuyInvoiceDuplicateButton,
                extraProps: {
                    title: buyInvoiceDuplicateText,
                    id: 'duplicateBuyInvoice',
                    allRowData: data,
                    permissionTy: 'buy.invoice',
                }
            },
            downloadBuyCsv: {
                component: BuyDownloadButtonCSV,
                extraProps: {
                    title: buyInvoiceDownloadCSVText,
                    id: 'downloadBuyCsv',
                    Order,
                    typeTemplate: 'buyInvoice'
                }
            },
            downloadBuyTag: {
                component: BuyDownloadTags,
                extraProps: {
                    title: buyInvoiceDownloadTagsText,
                    id: 'downloadBuyTag',
                    Order,
                    typeTemplate: 'buyInvoice'
                }
            },
            previewBuySupplierPDF: {
                component: BuySupplierPdfPreview,
                showButton: Order.supplierPdf?.length,
                extraProps: {
                    title: buyOrderPreviewSupplierPDFText,
                    id: 'previewBuyOrderPDFSupplier',
                    Order,
                    typeTemplate: 'buyOrder',
                    key: 'previewBuyOrderPDFSupplier'
                }
            },
            previewBuyInvoicePDF: {
                component: BuyPdfPreview,
                extraProps: {
                    title: buyInvoicePreviewPDFText,
                    id: 'previewBuyInvoicePDF',
                    Order,
                    typeTemplate: 'buyInvoice',
                    key: 'previewBuyInvoicePDF'
                }
            },
            downloadBuyTemplate: {
                component: BuyDownloadPdfTemplateButton,
                extraProps: {
                    title: buyInvoiceDownloadPDFTemplateText,
                    id: 'downloadBuyTemplate',
                    Order,
                    typeTemplate: 'buyInvoice',
                    key: 'downloadBuyInvoicePDFTemplate'
                }
            },
            downloadBuyInvoice: {
                component: BuyDownloadButtonPDF,
                extraProps: {
                    title: buyInvoiceDownloadPDFText,
                    id: 'downloadBuyInvoice',
                    Order,
                    typeTemplate: 'buyInvoice',
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: buyInvoiceDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: buyInvoiceVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
            buyProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: buyOrderProductTypesText,
                    id: 'buyProductTypes',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
        }
    }, [Order, buyInvoiceDivideIntoChaptersText, buyInvoiceDownloadCSVText, buyInvoiceDownloadPDFTemplateText, buyInvoiceDownloadPDFText, buyInvoiceDownloadTagsText, buyInvoiceDuplicateText, buyInvoicePreviewPDFText, data, onSubmit, isReadOnly, buyInvoiceVariablesText, buyOrderProductTypesText, buyOrderPreviewSupplierPDFText])

    return { name, ButtonComponents, defaultButtons }
}

export const BuyInvoiceFinishedStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useBuyInvoiceFinishedButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default BuyInvoiceFinishedStepButtons